import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Globe, Phone, MapPin, Briefcase, CreditCard, CheckCircle, XCircle } from 'lucide-react';
import api from '../../utils/api';

const STATUS_COLOR = {
  ativo:    'bg-emerald-100 text-emerald-700',
  expirado: 'bg-red-100 text-red-700',
  pendente: 'bg-yellow-100 text-yellow-700',
  bloqueado:'bg-gray-100 text-gray-500',
};

export default function AdminPerfilEmpresa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/empresas/${id}`)
      .then(r => setP(r.data))
      .catch(() => navigate('/admin/empresas'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-acre-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!p) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-500 hover:text-acre-dark mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>

        {/* Card principal */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-3xl font-bold text-white">
                {p.logo_url
                  ? <img src={p.logo_url} alt="logo" className="w-12 h-12 object-contain rounded-xl"/>
                  : <Building2 className="w-8 h-8"/>}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="font-sora font-bold text-xl text-acre-dark">{p.razao_social || p.nome}</h1>
                  {p.selo_verificado && <CheckCircle className="w-5 h-5 text-acre-dark/60"/>}
                </div>
                {p.nome_fantasia && <p className="text-yellow-900/70 text-sm">{p.nome_fantasia}</p>}
                <p className="text-yellow-900/60 text-xs mt-1">{p.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_COLOR[p.status_pagamento] || 'bg-gray-100 text-gray-500'}`}>
                    {p.status_pagamento}
                  </span>
                  {p.data_expiracao && (
                    <span className="text-xs bg-black/10 text-yellow-900/70 px-2.5 py-1 rounded-full">
                      Expira: {new Date(p.data_expiracao).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 grid sm:grid-cols-2 gap-4">
            {[
              { icon: <MapPin className="w-4 h-4"/>, label: 'Cidade', val: p.cidade },
              { icon: <Phone className="w-4 h-4"/>, label: 'Telefone', val: p.telefone },
              { icon: <Briefcase className="w-4 h-4"/>, label: 'Setor', val: p.setor },
              { icon: <Building2 className="w-4 h-4"/>, label: 'Porte', val: p.porte },
              { icon: <CreditCard className="w-4 h-4"/>, label: 'CNPJ', val: p.cnpj },
              { icon: <Globe className="w-4 h-4"/>, label: 'Website', val: p.website },
              { icon: <Phone className="w-4 h-4"/>, label: 'E-mail comercial', val: p.email_comercial },
            ].filter(i => i.val).map((i, k) => (
              <div key={k} className="flex items-center gap-3 text-sm">
                <span className="text-amber-500">{i.icon}</span>
                <div>
                  <p className="text-gray-400 text-xs">{i.label}</p>
                  {i.label === 'Website'
                    ? <a href={p.website?.startsWith('http')?p.website:`https://${p.website}`} target="_blank" rel="noreferrer" className="text-acre-green text-xs hover:underline">{p.website}</a>
                    : <p className="text-acre-dark font-medium">{i.val}</p>}
                </div>
              </div>
            ))}
            {p.descricao && (
              <div className="sm:col-span-2">
                <p className="text-gray-400 text-xs mb-1">Sobre a empresa</p>
                <p className="text-gray-600 text-sm leading-relaxed">{p.descricao}</p>
              </div>
            )}
          </div>
        </div>

        {/* Vagas */}
        {p.vagas?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4">
            <h2 className="font-sora font-bold text-acre-dark mb-4 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-amber-500"/> Vagas Publicadas
            </h2>
            <div className="space-y-2">
              {p.vagas.map(v => (
                <div key={v.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="font-medium text-acre-dark">{v.titulo}</p>
                    <p className="text-gray-400 text-xs">{v.cidade_vaga} · {v.modalidade} · {v.regime_contratacao}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    v.status === 'aberta' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                  }`}>{v.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagamentos */}
        {p.pagamentos?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-sora font-bold text-acre-dark mb-4 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-amber-500"/> Histórico de Pagamentos
            </h2>
            <div className="space-y-2">
              {p.pagamentos.map(pg => (
                <div key={pg.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="font-medium text-acre-dark">R$ {Number(pg.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p className="text-gray-400 text-xs">{new Date(pg.created_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    pg.status === 'aprovado' ? 'bg-emerald-100 text-emerald-700' :
                    pg.status === 'pendente' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                  }`}>{pg.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
